import { useCallback, useEffect, useMemo, useState } from "react"
import { flatten } from "shared/util"
import { KojinResult } from "interface/kojin"
import {
	PredictionCondition,
	PredictionData,
	usePredictionRequest
} from "interface/carenet/prediction"
import {
	SharedListUploadResult,
	usePostSharedList
} from "interface/carenet/sharedList"

type usePredictionProps = {
	searchId: number | null
	targetListId: number | null
	kojinList: KojinResult[]
	conditionText: string
	conditionHash: string
}
export const usePrediction = ({
	searchId,
	targetListId,
	kojinList,
	conditionText,
	conditionHash
}: usePredictionProps) => {
  const [isRetry, setRetry] = useState<boolean>(false)
  const [predictionCondition, setPredictionCondition] = useState<PredictionCondition | null>(null)

	const [uploadResult, setUploadResult] = useState<SharedListUploadResult | null>(null)
	const [uploadHistory, setUploadHistory] = useState<SharedListUploadResult[]>([])

	const [predictionResult, setPredictionResult] = useState<PredictionData | null>(null)
  const [predictionHistory, setPredictionHistory] = useState<PredictionData[]>([])

	const setPredictionConditionAndRetry = useCallback(
		(condition: PredictionCondition | null) => {
			setRetry(true)
			setPredictionCondition(condition)
		},
		[setPredictionCondition]
	)

	const onUploaded = useCallback(
		(result: SharedListUploadResult) => {
			setUploadResult(result)
			setUploadHistory([...uploadHistory].concat([result]))
		},
		[setUploadResult, uploadHistory]
	)
	const onCompletePrediction = useCallback(
    (result: PredictionData | null) => {
			if (result) {
				if (predictionHistory.find(history => (
					history.uploadResult.sharedList.sharedListId === result.uploadResult.sharedList.sharedListId &&
					history.predictionCondition.hash === result.predictionCondition.hash
				)) === undefined) {
					const history = [...predictionHistory, result]
					setPredictionHistory(history.length > 20 ? history.slice(history.length - 20) : history)
				}
			}
			setPredictionResult(result)
    },
    [predictionHistory, setPredictionHistory, setPredictionResult]
  )
	const { post: postSharedList, postState, postListState, uploadState } = usePostSharedList({ onPosted: onUploaded })
	const [predictionState, predictionRequest] = usePredictionRequest(searchId, { onFinished: onCompletePrediction })

	useEffect(
		() => {
			if (!searchId) return
			if (postState.posting || postListState.posting || uploadState.posting) return
			if (predictionCondition === null) return
			if (uploadResult?.hash === conditionHash) return
			
			const history = uploadHistory.find(history => history.hash === conditionHash)
			if (history) {
				setUploadResult(history)
			} else {
				postSharedList({
					form: {
						searchId,
						targetListId,
						condition: conditionText,
						description: ''
					},
					dataForm: {
						personalIdList: kojinList.flatMap(kojin => kojin.personalId ?? [])
					},
					hash: conditionHash
				})
			}
		},
		[
			searchId, targetListId, kojinList, conditionText, conditionHash,
			predictionCondition, postState, postListState, uploadState, postSharedList,
			uploadHistory, uploadResult
		]
	)

	useEffect(
		() => {
			if (predictionState.posting || (!isRetry && predictionState.error)) return
			if (!uploadResult || !predictionCondition) {
				onCompletePrediction(null)
				return
			}
			setRetry(false)
			if (
				predictionResult &&
				predictionResult.uploadResult.sharedList.sharedListId === uploadResult.sharedList.sharedListId &&
				predictionResult.predictionCondition.hash === predictionCondition.hash
			) return	// 条件変わらず

			const history = predictionHistory.find(
				history => (
          history.uploadResult.sharedList.sharedListId === uploadResult.sharedList.sharedListId &&
          history.predictionCondition.hash === predictionCondition.hash
				)
			)
			if (history) {
				onCompletePrediction(history)
				return
			}
			predictionRequest({ uploadResult, predictionCondition })
		},
		[
			uploadResult, predictionCondition,
			predictionResult, predictionHistory,
			predictionState.posting, predictionState.error,
			predictionRequest, onCompletePrediction, isRetry, setRetry
		]
	)

	const posting = useMemo(
		() => postState.posting || postListState.posting || uploadState.posting || predictionState.posting,
		[postState.posting, postListState.posting, uploadState.posting, predictionState.posting]
	)
	const errors = useMemo(
		() => flatten([ predictionState.error, postState.error, postListState.error, uploadState.error ]),
		[predictionState.error, postState.error, postListState.error, uploadState.error]
	)

	return useMemo(() => ({
		posting, errors,
		predictionCondition, setPredictionCondition: setPredictionConditionAndRetry,
		predictionResult, predictionHistory, setPredictionHistory
	}), [
		posting, errors,
		predictionCondition, setPredictionConditionAndRetry,
		predictionResult, predictionHistory, setPredictionHistory
	])
}